/**
 * Resolve the operating calendar for a `ConfigScope`.
 *
 * - Org-tier scope (no site): the district's default calendar.
 * - Site-tier scope: the site's own calendar IF it exists (an override);
 *   otherwise the inherited org default is reported, with `isInherited=true`
 *   so the editor can offer "create a site override".
 *
 * Returns enough for the editors to decide whether the child collections they
 * render belong to an editable own-calendar or a read-only inherited one.
 */

import { useMemo } from 'react';
import { useOperatingCalendars } from '../../hooks/queries';
import type { OperatingCalendar } from '../../services';
import type { ConfigScope } from './types';

export interface ScopedCalendarResult {
  /** The calendar that governs this scope (own row, or inherited org default). */
  calendar: OperatingCalendar | null;
  /** True when a site scope is falling back to the org default (no own row). */
  isInherited: boolean;
  /** True once the calendar shown is the scope's OWN editable row. */
  hasOwnCalendar: boolean;
  isLoading: boolean;
  error: unknown;
}

export function useScopedCalendar(scope: ConfigScope | null): ScopedCalendarResult {
  // Always fetch the org default; for a site scope also fetch the site's own.
  const orgQuery = useOperatingCalendars(
    scope ? { district: scope.districtId } : {},
  );
  const siteQuery = useOperatingCalendars(
    scope?.schoolId ? { school: scope.schoolId } : {},
  );

  return useMemo<ScopedCalendarResult>(() => {
    if (!scope) {
      return {
        calendar: null,
        isInherited: false,
        hasOwnCalendar: false,
        isLoading: false,
        error: null,
      };
    }

    // The operating-calendar list endpoint is unpaginated (bare array, not a
    // `{results}` envelope) — see operatingCalendar.service.
    const orgDefault = orgQuery.data?.[0] ?? null;

    // Org-tier scope: the district default IS the own calendar.
    if (!scope.schoolId) {
      return {
        calendar: orgDefault,
        isInherited: false,
        hasOwnCalendar: !!orgDefault,
        isLoading: orgQuery.isLoading,
        error: orgQuery.error,
      };
    }

    // Site-tier scope: prefer the site's own row; fall back to org default.
    const siteOwn = siteQuery.data?.[0] ?? null;
    if (siteOwn) {
      return {
        calendar: siteOwn,
        isInherited: false,
        hasOwnCalendar: true,
        isLoading: siteQuery.isLoading,
        error: siteQuery.error,
      };
    }
    return {
      calendar: orgDefault,
      isInherited: !!orgDefault,
      hasOwnCalendar: false,
      isLoading: orgQuery.isLoading || siteQuery.isLoading,
      error: orgQuery.error ?? siteQuery.error,
    };
  }, [scope, orgQuery.data, orgQuery.isLoading, orgQuery.error, siteQuery.data, siteQuery.isLoading, siteQuery.error]);
}
